import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import AssignmentSettings from '../../../../components/AssignmentSettings';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import CustomToast from '../../../../components/Toast';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import PropTypes from 'prop-types';
import { useChangeAssessmentModeMutation, useValidateChangeModeMutation } from '../../../../features/authentication/assessmentApiSlice';
import { useAssignmentContext } from '../../context';
import moment from 'moment';
import ConfirmationModal from '../../../../components/ConfirmationPopUp';
import InfoModal from '../../../../components/InfoPopUpModal';

const AssignSettingsPopout = ({ isOpen, onClose, selectedAssessments }) => {
  const [isChangingMode, setIsChangingMode] = useState(false);
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState(false);
  const [warningText, setWarningText] = useState('');
  const [blockedAssessments, setBlockedAssessments] = useState([]);
  const [isOpenInfoModal, setIsOpenInfoModal] = useState(false);

  const assignmentContext = useAssignmentContext();
  const [validateChangeMode] = useValidateChangeModeMutation();
  const [changeAssessmentMode] = useChangeAssessmentModeMutation();

  const generateAssessmentSettings = () => {
    const timedTimeSpan = parseInt(assignmentContext.timedHour) * 60 + parseInt(assignmentContext.timedMinute);

    return {
      assessmentIds: selectedAssessments.map(a => a.assignmentId),
      students: [],
      selfAssign: false,
      availability: assignmentContext.availabilityOption,
      assignmentMode:
        assignmentContext.assignmentMode === '' ? 'open' : assignmentContext.assignmentMode,
      allowAnswerChange: assignmentContext.allowAnswerChange,
      showGrade: assignmentContext.showGrade,
      startDate:
        assignmentContext.startDate != ''
          ? moment(assignmentContext.startDate).format('YYYY/MM/DD h:mm A')
          : '',
      endDate:
        assignmentContext.endDate != ''
          ? moment(assignmentContext.endDate).format('YYYY/MM/DD h:mm A')
          : '',
      studentFileUpload: assignmentContext.studentFileUpload,
      timed: assignmentContext.isTimed,
      timeSpan: timedTimeSpan,
      practice: true,
      assignRetake: assignmentContext.retakesEnabled,
      masteryLevel: assignmentContext.masteryValue,
      retakeMode: assignmentContext.assignmentMode,
      scoreRetries: assignmentContext.scoreRetries,
      retakeLimit: assignmentContext.retakeLimitValue,
      retakeAvailability: assignmentContext.availabilityOption,
      retakeStartDate:
        assignmentContext.startDate != ''
          ? moment(assignmentContext.startDate).format('YYYY/MM/DD h:mm A')
          : '',
      retakeEndDate:
        assignmentContext.endDate != ''
          ? moment(assignmentContext.endDate).format('YYYY/MM/DD h:mm A')
          : '',
      retakeTimed: assignmentContext.isTimed,
      retakeTimeSpan: timedTimeSpan,
      retakeQuestion: assignmentContext.retakeQuestion,
      includeCR: assignmentContext.includeCR,
      autoLockQuestions: assignmentContext.autoLockQuestions,
      allowLangChange: assignmentContext.allowLangChange,
      useCalc: assignmentContext.calculatorEnabled ? 1 : 0,
      useProtractor: assignmentContext.useProtractor ? 1 : 0,
      useRuler: assignmentContext.useRuler ? 1 : 0,
      timeOnTask: assignmentContext.timeOnTaskEnabled,
      period: assignmentContext.timeOnTaskSeconds,
      lockBrowser: assignmentContext.lockBrowser,
      photoMathBlock: assignmentContext.photoMathBlock,
      autoStartCRUponGrading: assignmentContext.autoStartCRUponGrading,
      cranswerToolId: assignmentContext.crAnswerToolType,
      calculatorType: assignmentContext.calculatorType
    };
  };

  const handleChangeMode = async () => {
    const timedTimeSpan = parseInt(assignmentContext.timedHour) * 60 + parseInt(assignmentContext.timedMinute);

    if (assignmentContext.availabilityOption !== 'offline') {
      if (!assignmentContext.assignmentMode) {
        CustomToast('Please select the assignment mode', 'info');
        return;
      }
    }
    if (assignmentContext.isTimed && timedTimeSpan <= 0) {
      CustomToast('The time length must be greater than 0 minutes.', 'info');
      return;
    }
    if (assignmentContext.availabilityOption == 'FromTo') {
      const currentDate = moment().format('YYYY/MM/DD h:mm A');
      if (moment(assignmentContext.startDate).isBefore(currentDate, 'day')) {
        CustomToast('From Date must not be before the current day', 'error');
        return;
      }
      if (moment(assignmentContext.endDate).isBefore(currentDate)) {
        CustomToast('To Date must not be before the current date/time', 'error');
        return;
      }
      if (moment(assignmentContext.endDate).isBefore(moment(assignmentContext.startDate)) || moment(assignmentContext.endDate).isSame(moment(assignmentContext.startDate))) {
        CustomToast('To Date must be after the From Date', 'error');
        return;
      }
    }

    if (assignmentContext.retakesEnabled) {
      if (assignmentContext.retakeLimitValue <= 0) {
        CustomToast('You must select a retake limit', 'info');
        return;
      }
      if (assignmentContext.retakeQuestion === null) {
        CustomToast(
          'You must select an option for Auto Graded Questions to include on Retakes',
          'info'
        );
        return;
      }
      if (assignmentContext.includeCR === null) {
        CustomToast('You must select an option for CR Questions to include on Retakes', 'info');
        return;
      }
    }

    try {
      var response = await validateChangeMode(generateAssessmentSettings());

      if (response.error) {
        if (response.error.data.errors)
          CustomToast(response.error.data.errors.AssessmentIds, 'error');
        else
          CustomToast(response.error.data.error, 'error');
      } else {
        var blockedAssessments = response.data;

        if (blockedAssessments.length > 0) {
          setBlockedAssessments(blockedAssessments);

          const groupWarnings = blockedAssessments.reduce((acc, item) => {
            if (!acc[item.name]) {
              acc[item.name] = [];
            }
            acc[item.name].push(item.id);
            return acc;
          }, {});

          const reasons = Object.entries(groupWarnings).map(([name, ids], index) => {
            const assessmentNames = selectedAssessments
              .filter(x => ids.includes(x.assignmentId))
              .map(x => x.assignmentName)
              .join(', ');

            return (
              <OverlayTrigger
                key={index}
                placement="right"
                overlay={
                  <Tooltip className="custom-tooltip">
                    {assessmentNames}
                  </Tooltip>
                }
              >
                <p className="text-danger cursor-pointer w-fitcontent">{name} {`(${ids.length})`}</p>
              </OverlayTrigger>
            );
          });

          let commonString = `You have selected ${selectedAssessments.length} assessment${selectedAssessments.length == 1 ? '' : 's'}, `;

          const warningText = (
            <div>
              <p className='cursor-none'>
                {commonString} and {' '}
                {blockedAssessments.length === selectedAssessments.length ? 'both' : blockedAssessments.length} of them cannot Change Mode.
                <br />
              </p>
              <p className="text-lg-start">
                <strong className="cursor-none">Reason{selectedAssessments.length == 1 ? '' : 's'}:</strong>
                {reasons}
              </p>
            </div>
          );

          const warningTextWithContinue = (
            <>
              {warningText}
              <p className='fw-semibold'>Do you want to continue?</p>
            </>
          );

          if (selectedAssessments.length === 1 && blockedAssessments.length === 1) {
            onClose();
            CustomToast(commonString + ` and it cannot Change Mode as ${blockedAssessments[0].name}`, 'error');
          } else if (selectedAssessments.length == blockedAssessments.length) {
            setWarningText(warningText);
            setIsOpenInfoModal(true);
          } else {
            setWarningText(warningTextWithContinue);
            setIsOpenConfirmModal(true);
          }
        } else {
          handleConfirmChangeMode();
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleConfirmChangeMode = async () => {
    var options = generateAssessmentSettings();

    if (isOpenConfirmModal) {
      setIsOpenConfirmModal(false);

      const blockedAssessmentIds = blockedAssessments.map(x => x.id);
      options.assessmentIds = options.assessmentIds.filter(
        id => !blockedAssessmentIds.includes(id)
      );
    }

    setIsChangingMode(true);
    try {
      var response = await changeAssessmentMode(options);

      if (response.error) {
        if (response.error.data.errors)
          CustomToast(response.error.data.errors.AssessmentIds, 'error');
        else
          CustomToast(response.error.data.error, 'error');
      } else {
        CustomToast('Assessment Settings Updated Successfully!', 'success');
      }
      onClose();
      setIsChangingMode(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleInfoModalSave = () => {
    onClose();
    setIsOpenInfoModal(false);
  };

  return (
    <>
      <div className={`assignmentSettingsPopOut ${isOpen ? '' : 'hidden'}`}>
        <AssignmentSettings
          allowRetakesOption={false}
          headingDescription={false}
          availabilityRequired={true}
          modesRequired={true}
          showBetaButton={true}
          noColumn={true}
          randomizedQuestion={true}
          disabledTextSpeach={true}
          allowOfflineMode={true}
          isSelfAssign={false}
          isAssessment={true}
        />

        <div className="assignButtonsRow gap-3 h-100">
          <Button
            className="btn-width"
            variant="castleGray"
            onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            className='btn-width d-flex align-items-center'
            variant="castleRed"
            onClick={() => handleChangeMode()}
            disabled={isChangingMode}>
            {isChangingMode ? 'Changing Mode...' : 'Change Mode'}
          </Button>
        </div>

        <div className="assignmentSettingsCloseBtn">
          <FontAwesomeIcon
            className="ms-2"
            size="2x"
            icon={faXmark}
            onClick={() => onClose()}
          />
        </div>
      </div>

      {isOpenConfirmModal &&
        <ConfirmationModal
          isOpen={isOpenConfirmModal}
          onClose={() => setIsOpenConfirmModal(false)}
          titleName={'Change Mode Confirmation'}
          onSave={() => handleConfirmChangeMode()}
          contentWidth={'40%'}
          buttonPosition={'center'}
          content={warningText}
          cancelButton={'No'}
          confirmButton={'Yes'}
          isRawHtmlContent={false}
        />
      }

      {isOpenInfoModal &&
        <InfoModal
          isOpen={isOpenInfoModal}
          onClose={() => setIsOpenInfoModal(false)}
          title={'Change Assessment Settings'}
          content={warningText}
          contentWidth={'40%'}
          buttonPosition={'center'}
          buttonText={'OK'}
          onSave={() => handleInfoModalSave()}
        />
      }
    </>
  );
};

AssignSettingsPopout.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedAssessments: PropTypes.array.isRequired
};

export default AssignSettingsPopout;
